import * as io from "io-ts";
import cx from "classnames";
import React, {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  type MouseEvent,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import createGlobalState from "./lib/createGlobalState";
import { colors, spacings } from "./Theme";
import { css } from "@linaria/core";
import { Text } from "./Text";
import { ArrowRightSLine } from "./IconV2/react";
import t from "./i18n";

const useGroupExpension = createGlobalState({
  storage: "local",
  key: "groupExpension",
  type: io.array(io.string),
  scoped: true,
});

const GroupExpensionContext = createContext<{
  isExpended: (group: unknown) => boolean;
  toggleExpended: (group: unknown) => void;
  size: (group: unknown) => number;
  groupCount: number;
}>({
  isExpended: () => true,
  toggleExpended: () => {},
  size: () => 0,
  groupCount: 0,
});

function expendedKey(groupedBy: string | null, group: unknown) {
  return `${groupedBy}-${group}`;
}

export function GroupList<K, V>({
  scope,
  groupedBy,
  groupedItems,
  children,
  updateGroupedItems,
}: {
  updateGroupedItems: (itemsInGroup: V[], selectedItems: V[]) => void;
  scope: string;
  groupedBy: string | null;
  groupedItems: [K, V[]][];
  children: (group: K, items: V[], updateSelectedItems: (selectedItems: V[]) => void) => ReactNode;
}) {
  const [expendedGroups, setExpendedGroups] = useGroupExpension({
    scope,
    defaultValue: groupedItems.map(([group]) => expendedKey(groupedBy, group)),
  });

  function isExpended<G>(group: G) {
    return expendedGroups.some(g => g === expendedKey(groupedBy, group));
  }

  function size(group: unknown): number {
    return groupedItems.find(([g]) => g === group)?.[1]?.length ?? 0;
  }

  function toggleExpended<G>(group: G) {
    setExpendedGroups(groups => {
      if (isExpended(group)) {
        return groups.filter(g => g !== expendedKey(groupedBy, group));
      }
      return Array.from(new Set([...groups, expendedKey(groupedBy, group)]));
    });
  }

  /** @note handle groupedBy changes default state */
  useEffect(
    () =>
      setExpendedGroups(expendedGroups => {
        if (!groupedBy) {
          return [expendedKey(groupedBy, null)];
        } else if (expendedGroups.some(g => g.includes(groupedBy))) {
          return expendedGroups;
        } else {
          return groupedItems.map(([group]) => expendedKey(groupedBy, group));
        }
      }),
    [groupedBy, groupedItems, setExpendedGroups],
  );

  const updateSelectedItemsByGroup = useMemo(
    () =>
      new Map(
        groupedItems.map(([group, itemsInGroup]) => [
          group,
          (selectedItems: V[]) => updateGroupedItems(itemsInGroup, selectedItems),
        ]),
      ),
    [groupedItems, updateGroupedItems],
  );

  return (
    <GroupExpensionContext.Provider
      value={{ isExpended, toggleExpended, size, groupCount: groupedItems.length }}
    >
      {groupedItems.map(([group, items]) => {
        const updateSelectedItems = updateSelectedItemsByGroup.get(group);
        if (!updateSelectedItems) throw new Error("Missing group selection updater");

        return (
          <Fragment key={expendedKey(groupedBy, group)}>
            {children(group, items, updateSelectedItems)}
          </Fragment>
        );
      })}
    </GroupExpensionContext.Provider>
  );
}

const GroupItemContext = createContext<unknown>(null);

export function GroupItem<G>({ group, children }: PropsWithChildren<{ group: G }>) {
  const { isExpended, groupCount } = useContext(GroupExpensionContext);
  return (
    <GroupItemContext.Provider value={group}>
      {groupCount === 1 ? children : <details open={isExpended(group)}>{children}</details>}
    </GroupItemContext.Provider>
  );
}

export function GroupToggle({ children }: { children: ReactNode }) {
  const { isExpended, toggleExpended, size, groupCount } = useContext(GroupExpensionContext);
  const group = useContext(GroupItemContext);

  const toggle = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    toggleExpended(group);
  };

  if (groupCount === 1) return null;

  return (
    <summary onClick={toggle} className={cx(groupToggle, { expended: isExpended(group) })}>
      <ArrowRightSLine size="m" color={colors.carbon800} className="indicator" />
      {children ?? <Text type="body1">{t("group.no_group")}</Text>}{" "}
      <Text type="body1">({size(group)})</Text>
    </summary>
  );
}

const groupToggle = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
  cursor: pointer;
  padding: ${spacings.m} 0;
  color: ${colors.carbon900};

  &.expended > svg.indicator {
    transform: rotateZ(90deg);
  }
`;
